/**
 * MODULE: EmailTemplateEditFormCatalogBridge.tsx
 * PURPOSE: Route assistant email_template edit form_catalog to golden-path EmailTemplateEditModal.
 */

import { requestOpenEmailTemplateEdit } from '@shared/lib/frontend/utils/emailTemplateEditBridge';
import type { FormRequest } from '../../hooks/assistant/types';
import { extractEmailTemplateIdFromFormRequest } from './assistantFormRequestUtils';

type EmailTemplateEditFormCatalogBridgeProps = {
  formRequest: FormRequest;
  onCancel: () => void;
  disabled?: boolean;
};

export function EmailTemplateEditFormCatalogBridge({
  formRequest,
  onCancel,
  disabled = false,
}: EmailTemplateEditFormCatalogBridgeProps) {
  const templateId = extractEmailTemplateIdFromFormRequest(formRequest);
  const templateName =
    (formRequest.fields.find((f) => f.name === 'template_name')?.default as string | undefined)?.trim() ||
    (formRequest.continuation?.collected_fields?.template_name as string | undefined)?.trim() ||
    '';

  const openEditor = () => {
    if (!templateId) return;
    requestOpenEmailTemplateEdit({ templateId, coachInAssistant: true });
    onCancel();
  };

  return (
    <div className="space-y-3" data-testid="assistant-email-template-edit-golden-path">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
        Edit Email Template{templateName ? `: ${templateName}` : ''}
      </h3>
      <div
        className="rounded-lg border border-violet-200 bg-violet-50/80 p-3 dark:border-violet-900/50 dark:bg-violet-950/30"
        data-testid="assistant-email-template-edit-golden-path-banner"
      >
        <p className="text-xs text-violet-900 dark:text-violet-100">
          Open the full template editor to update subject, merge tags, and body — changes sync to
          Assistant sends and Messaging sequences.
        </p>
        <button
          type="button"
          disabled={disabled || !templateId}
          onClick={openEditor}
          className="mt-2 w-full rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-violet-700 disabled:opacity-50"
          data-testid="assistant-email-template-open-edit-editor"
        >
          Open template editor
        </button>
        {!templateId ? (
          <p className="mt-2 text-[11px] text-amber-800 dark:text-amber-200">
            Template id missing — try naming the template again (e.g. &quot;edit the Intro template&quot;).
          </p>
        ) : null}
      </div>
    </div>
  );
}
